import { FC, useCallback, useEffect, useRef, useState } from 'react';
import MapboxGlMap, { Layer, Marker, Source } from 'react-map-gl';
import type { MapRef, TransformRequestFunction } from 'react-map-gl';
import { Box, IconButton } from '@mui/material';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RemoveIcon from '@mui/icons-material/Remove';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import MapIcon from '@mui/icons-material/Map';
import TimelineIcon from '@mui/icons-material/Timeline';
import PlaceIcon from '@mui/icons-material/Place';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../types';
import {
    currentImageIndexQuery,
    positionResolvedQuery,
    sessionIdQuery,
    sessionQuery,
} from '../../../../../core/streetViewer/queries';
import { imagesSelector } from '../../../../../core/legacy/selectors/streetView';
import { useConfig } from '../../../../config';
import { SOURCES } from '../../../../../providers/map';
import {
    DEGRADATIONS_SOURCE_ID,
    IMAGES_SOURCE_ID,
    ROADS_SOURCE_ID,
    WIDTHS_SOURCE_ID,
} from '../../../../../core/domain/map';
import { getLayerConfig } from '../../../../network-map/Map/network-map-layer-configuration';
import {
    getSessionLayerConfig,
    SOURCE_NETWORK_SESSION_ID,
} from '../../../../network-map/Map/network-map-session-layer-configuration';
import {
    DEGRADATIONS_FILL_LAYER,
    ROAD_DATA_LINESTRING_LAYER,
} from '../../../../network-map/Map/network-map-road-data-layer-configuration';
import PinImage from '../../../../network-map/Map/Image';
import {
    PR_LAYER,
    PR_LAYER_PAINT_SATELLITE,
    PR_LAYER_PAINT_STREET,
    PR_REUNION_LAYER,
    PR_REUNION_SOURCE_ID,
    PR_REUNION_SOURCE_URL,
    PR_SOURCE_ID,
    PR_SOURCE_URL,
    hasPrReunionLayer,
} from '../../../../network-map/Map/pr-layer-configuration';
import {
    displayDegradationsQuery,
    displayPrQuery,
    displayRoadsQuery,
    networkMapQuery,
    selectedGradeReferenceQuery,
    selectedmapStyleQuery,
    sessionNetworkQuery,
} from '../../../../../core/networkMap/queries';
import { LoadSessionNetworkDataCommand } from '../../../../../core/networkMap/commands/LoadSessionNetworkDataCommand';
import { UpdateDisplayDegradationsCommand } from '../../../../../core/networkMap/commands/UpdateDisplayDegradationsCommand';
import { UpdateDisplayPrCommand } from '../../../../../core/networkMap/commands/UpdateDisplayPrCommand';
import { UpdateDisplayRoadsCommand } from '../../../../../core/networkMap/commands/UpdateDisplayRoadsCommand';
import { UpdateMapStyleCommand } from '../../../../../core/networkMap/commands/UpdateMapStyleCommand';
import { SetMapStyleFromPersistenceCommand } from '../../../../../core/networkMap/commands/SetMapStyleFromPersistenceCommand';
import { SetDisplayDegradationsFromPersistenceCommand } from '../../../../../core/networkMap/commands/SetDisplayDegradationsFromPersistenceCommand';
import { SetDisplayPrFromPersistenceCommand } from '../../../../../core/networkMap/commands/SetDisplayPrFromPersistenceCommand';
import { SetDisplayRoadsFromPersistenceCommand } from '../../../../../core/networkMap/commands/SetDisplayRoadsFromPersistenceCommand';
import pinIcon from '../../../../../assets/icons/pin.svg';
import type { Image } from '../../../../../core/domain/Session';
import type GeoJSON from 'geojson';
import { useDraggableWithinParent } from '../../../../../utils/use-draggable-within-parent';

const computeBearing = (from: [number, number], to: [number, number]): number => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const [lng1, lat1] = [toRad(from[0]), toRad(from[1])];
    const [lng2, lat2] = [toRad(to[0]), toRad(to[1])];
    const dLng = lng2 - lng1;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
};

const DirectionMarker: FC<{ bearing: number }> = ({ bearing }) => (
    <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        style={{ transform: `rotate(${bearing}deg)`, display: 'block' }}
    >
        <circle cx="14" cy="14" r="13" fill="#1D3875" stroke="white" strokeWidth="2" />
        <polygon points="14,3 20,17 14,13 8,17" fill="white" />
    </svg>
);

const bearingFromSessionGeoJson = (
    geoJson: GeoJSON.FeatureCollection,
    sessionId: string,
    pos: { x: number; y: number },
): number => {
    const feature = geoJson.features.find(
        (f) => f.geometry.type === 'LineString' && f.properties?.id === sessionId,
    );
    if (!feature || feature.geometry.type !== 'LineString') return 0;
    const coords = feature.geometry.coordinates as [number, number][];
    if (coords.length < 2) return 0;

    let minDist = Infinity;
    let closestIdx = 0;
    for (let i = 0; i < coords.length; i++) {
        const dx = coords[i][0] - pos.x;
        const dy = coords[i][1] - pos.y;
        const dist = dx * dx + dy * dy;
        if (dist < minDist) {
            minDist = dist;
            closestIdx = i;
        }
    }

    return closestIdx < coords.length - 1
        ? computeBearing(coords[closestIdx], coords[closestIdx + 1])
        : computeBearing(coords[closestIdx - 1], coords[closestIdx]);
};

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const BTN_SX = {
    position: 'absolute',
    zIndex: 11,
    bgcolor: 'white',
    '&:hover': { bgcolor: 'grey.100' },
    padding: '4px',
    boxShadow: 1,
} as const;

const MiniMapPiP: FC = () => {
    const mapRef = useRef<MapRef | null>(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { mapboxToken, backendUrl } = useConfig();
    const positionResolved = useAppSelector(positionResolvedQuery);
    const currentIndex = useAppSelector(currentImageIndexQuery);
    const sessionId = useAppSelector(sessionIdQuery);
    const session = useAppSelector(sessionQuery);
    const images = useAppSelector(imagesSelector) as Map<number, Image>;
    const selectedGradeReference = useAppSelector(selectedGradeReferenceQuery);
    // the whole organization's network when the full map loaded it, otherwise just this session's
    const organizationNetworkData = useAppSelector(networkMapQuery);
    const sessionNetworkData = useAppSelector(sessionNetworkQuery(sessionId ?? ''));
    const networkData = organizationNetworkData ?? sessionNetworkData;

    const mapStyle = useAppSelector(selectedmapStyleQuery);
    const displayRoads = useAppSelector(displayRoadsQuery);
    const displayPr = useAppSelector(displayPrQuery);
    const displayDegradations = useAppSelector(displayDegradationsQuery);
    const isSatellite = mapStyle === 'SATELLITE';
    const [isMinimized, setIsMinimized] = useState(false);
    const { ref: dragRef, offset: dragOffset, handleDragStart } = useDraggableWithinParent();

    useEffect(() => {
        dispatch(SetMapStyleFromPersistenceCommand());
        dispatch(SetDisplayPrFromPersistenceCommand());
        dispatch(SetDisplayRoadsFromPersistenceCommand());
        dispatch(SetDisplayDegradationsFromPersistenceCommand());
    }, []);

    // the mini map is displayed without going through the full map, which is what normally loads the
    // network data: load this session's own data, otherwise its line stays empty
    useEffect(() => {
        if (sessionId) {
            dispatch(LoadSessionNetworkDataCommand({ sessionId }));
        }
    }, [sessionId]);

    const handleToggleSatellite = () => {
        dispatch(UpdateMapStyleCommand({ mapStyle: isSatellite ? 'STREETS' : 'SATELLITE' }));
    };
    const handleToggleRoads = () => {
        dispatch(UpdateDisplayRoadsCommand({ display: !displayRoads }));
    };
    const handleTogglePr = () => {
        dispatch(UpdateDisplayPrCommand({ display: !displayPr }));
    };
    const handleToggleDegradations = () => {
        dispatch(UpdateDisplayDegradationsCommand({ display: !displayDegradations }));
    };

    const currentImage = images.get(currentIndex);
    const nextImage = images.get(currentIndex + 1);
    const prevImage = images.get(currentIndex - 1);

    // geom.coordinates is { x: longitude, y: latitude } (PostGIS/TypeORM convention)
    const coords = currentImage?.geom?.coordinates as { x: number; y: number } | undefined;
    const nextCoords = nextImage?.geom?.coordinates as { x: number; y: number } | undefined;
    const prevCoords = prevImage?.geom?.coordinates as { x: number; y: number } | undefined;

    // When next image exists, bearing is current→next. At the last image, use prev→current so
    // the arrow still points forward in the direction of travel rather than reversing.
    // On first load when adjacent images aren't in Redux yet, fall back to the session GeoJSON.
    const bearing =
        coords && nextCoords
            ? computeBearing([coords.x, coords.y], [nextCoords.x, nextCoords.y])
            : coords && prevCoords
              ? computeBearing([prevCoords.x, prevCoords.y], [coords.x, coords.y])
              : coords && sessionId && networkData?.sessionsData
                ? bearingFromSessionGeoJson(
                      networkData.sessionsData as GeoJSON.FeatureCollection,
                      sessionId,
                      coords,
                  )
                : 0;

    useEffect(() => {
        if (mapRef.current && coords) {
            mapRef.current.flyTo({ center: [coords.x, coords.y], duration: 400 });
        }
    }, [currentIndex]);

    // reuseMaps hands over a map instance whose canvas may still be sized for whatever
    // container last owned it (e.g. the full-page NetworkMap). Force it to match this
    // box's actual size whenever it (re)appears, otherwise it can render blank until
    // something else happens to trigger a resize.
    useEffect(() => {
        if (!isMinimized) {
            mapRef.current?.resize();
        }
    }, [isMinimized]);

    const transformRequest: TransformRequestFunction = useCallback(
        (url) => {
            if (url.startsWith(backendUrl!)) {
                return { url, credentials: 'include' };
            }
            return { url };
        },
        [backendUrl],
    );

    if (!positionResolved || !coords) return null;

    const sessionGeoJson = (networkData?.sessionsData ?? EMPTY_GEOJSON) as GeoJSON.FeatureCollection;
    // Only show the current session's path — the source otherwise carries every
    // session in the network, which clutters the mini map with unrelated lines.
    const currentSessionGeoJson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: sessionGeoJson.features.filter((f) => f.properties?.id === sessionId),
    };

    const wrapperSx = {
        position: 'absolute',
        bottom: 16,
        left: 16,
        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        zIndex: 10,
    } as const;

    if (isMinimized) {
        return (
            <Box sx={wrapperSx}>
                <IconButton
                    onClick={() => setIsMinimized(false)}
                    sx={{
                        bgcolor: 'white',
                        border: '2px solid white',
                        boxShadow: 4,
                        '&:hover': { bgcolor: 'grey.100' },
                    }}
                >
                    <MapIcon />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box
            ref={dragRef}
            sx={{
                ...wrapperSx,
                width: 320,
                height: 180,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: 4,
                border: '2px solid white',
                // Mapbox only auto-crops its logo to icon-only below 250px container
                // width; force that same crop here since we're wider than that.
                '& .mapboxgl-ctrl-logo': {
                    width: '23px',
                },
            }}
        >
            {/* Transparent drag handle strip across the top — above the map, below buttons */}
            <Box
                onMouseDown={handleDragStart}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 36,
                    zIndex: 10,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                }}
            />
            {/* Minimize — top left */}
            <IconButton size="small" onClick={() => setIsMinimized(true)} sx={{ ...BTN_SX, top: 6, left: 6 }}>
                <RemoveIcon fontSize="small" />
            </IconButton>
            {/* Degradations toggle — fifth from right */}
            <IconButton
                size="small"
                onClick={handleToggleDegradations}
                sx={{ ...BTN_SX, top: 6, right: 134, bgcolor: displayDegradations ? 'primary.main' : 'white', color: displayDegradations ? 'white' : 'inherit', '&:hover': { bgcolor: displayDegradations ? 'primary.dark' : 'grey.100' } }}
            >
                <BlurOnIcon fontSize="small" />
            </IconButton>
            {/* PR toggle — fourth from right */}
            <IconButton
                size="small"
                onClick={handleTogglePr}
                sx={{ ...BTN_SX, top: 6, right: 102, bgcolor: displayPr ? 'primary.main' : 'white', color: displayPr ? 'white' : 'inherit', '&:hover': { bgcolor: displayPr ? 'primary.dark' : 'grey.100' } }}
            >
                <PlaceIcon fontSize="small" />
            </IconButton>
            {/* Referential (roads) toggle — third from right */}
            <IconButton
                size="small"
                onClick={handleToggleRoads}
                sx={{ ...BTN_SX, top: 6, right: 70, bgcolor: displayRoads ? 'primary.main' : 'white', color: displayRoads ? 'white' : 'inherit', '&:hover': { bgcolor: displayRoads ? 'primary.dark' : 'grey.100' } }}
            >
                <TimelineIcon fontSize="small" />
            </IconButton>
            {/* Satellite toggle — second from right */}
            <IconButton
                size="small"
                onClick={handleToggleSatellite}
                sx={{ ...BTN_SX, top: 6, right: 38 }}
            >
                {isSatellite ? <MapIcon fontSize="small" /> : <SatelliteAltIcon fontSize="small" />}
            </IconButton>
            {/* Expand to full map — top right */}
            <IconButton
                size="small"
                onClick={() => {
                    // Carry over exactly what's currently shown in the mini map (center +
                    // zoom the user may have panned/zoomed to) so the full map opens on
                    // that same view instead of fitting the whole session's bounding box.
                    const map = mapRef.current?.getMap();
                    const center = map?.getCenter();
                    const zoom = map?.getZoom();
                    const width = map?.getContainer().clientWidth;
                    const params = new URLSearchParams({ sessionId: sessionId ?? '' });
                    if (center && zoom !== undefined) {
                        params.set('lat', String(center.lat));
                        params.set('lng', String(center.lng));
                        params.set('zoom', String(zoom));
                        // This box's own width — the full map uses it to compensate zoom for
                        // its much larger viewport (see NetworkMap's initializeMap).
                        if (width) params.set('w', String(width));
                    }
                    navigate(`/map?${params.toString()}`);
                }}
                sx={{ ...BTN_SX, top: 6, right: 6 }}
            >
                <OpenInFullIcon fontSize="small" />
            </IconButton>
            <MapboxGlMap
                reuseMaps
                ref={mapRef}
                mapboxAccessToken={mapboxToken}
                mapStyle={
                    isSatellite
                        ? 'mapbox://styles/mapbox/satellite-streets-v12'
                        : 'mapbox://styles/mapbox/streets-v9'
                }
                initialViewState={{
                    longitude: coords.x,
                    latitude: coords.y,
                    zoom: 16,
                }}
                style={{ width: '100%', height: '100%' }}
                transformRequest={transformRequest}
                attributionControl={false}
            >
                <PinImage name="pin" src={pinIcon} height={50} width={50} sdf />
                <Source id={SOURCE_NETWORK_SESSION_ID} type="geojson" data={currentSessionGeoJson} generateId />
                {Object.entries(SOURCES).map(([sourceId, { tilesPath, minZoom, maxZoom }]) => (
                    <Source
                        key={sourceId}
                        id={sourceId}
                        type="vector"
                        tiles={[`${backendUrl}${tilesPath}`]}
                        minzoom={minZoom}
                        maxzoom={maxZoom}
                        promoteId="id"
                    />
                ))}
                <Layer
                    id="mini-roads"
                    source={ROADS_SOURCE_ID}
                    source-layer={ROADS_SOURCE_ID}
                    type="line"
                    paint={{ 'line-color': 'rgb(0,0,0)', 'line-width': 3 }}
                    layout={{ visibility: displayRoads ? 'visible' : 'none' }}
                />
                <Layer
                    {...getSessionLayerConfig(selectedGradeReference)}
                    id="mini-session"
                    source={SOURCE_NETWORK_SESSION_ID}
                />
                <Layer
                    {...getLayerConfig(selectedGradeReference)}
                    id="mini-images"
                    source={IMAGES_SOURCE_ID}
                    source-layer={IMAGES_SOURCE_ID}
                    filter={['==', ['get', 'sessionId'], sessionId ?? '']}
                />
                <Layer
                    {...ROAD_DATA_LINESTRING_LAYER}
                    id="mini-road-data"
                    source={WIDTHS_SOURCE_ID}
                    source-layer={WIDTHS_SOURCE_ID}
                    filter={['==', ['get', 'sessionId'], sessionId ?? '']}
                />
                <Layer
                    {...DEGRADATIONS_FILL_LAYER}
                    id="mini-degradations"
                    source={DEGRADATIONS_SOURCE_ID}
                    source-layer={DEGRADATIONS_SOURCE_ID}
                    layout={{ visibility: displayDegradations ? 'visible' : 'none' }}
                    filter={['==', ['get', 'sessionId'], sessionId ?? '']}
                />
                <Source id={PR_SOURCE_ID} type="vector" url={PR_SOURCE_URL}>
                    <Layer
                        {...PR_LAYER}
                        id="mini-pr"
                        layout={{ ...PR_LAYER.layout, visibility: displayPr ? 'visible' : 'none' }}
                        paint={isSatellite ? PR_LAYER_PAINT_SATELLITE : PR_LAYER_PAINT_STREET}
                    />
                </Source>
                {hasPrReunionLayer && (
                    <Source id={PR_REUNION_SOURCE_ID} type="vector" url={PR_REUNION_SOURCE_URL}>
                        <Layer
                            {...PR_REUNION_LAYER}
                            id="mini-pr-reunion"
                            layout={{ ...PR_REUNION_LAYER.layout, visibility: displayPr ? 'visible' : 'none' }}
                            paint={isSatellite ? PR_LAYER_PAINT_SATELLITE : PR_LAYER_PAINT_STREET}
                        />
                    </Source>
                )}
                <Marker longitude={coords.x} latitude={coords.y}>
                    <DirectionMarker bearing={bearing} />
                </Marker>
            </MapboxGlMap>
            {session?.captureDate && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 6,
                        right: 8,
                        zIndex: 11,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        color: 'white',
                        fontSize: '11px',
                        borderRadius: '3px',
                        px: '6px',
                        py: '2px',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                >
                    {new Date(session.captureDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                </Box>
            )}
        </Box>
    );
};

export default MiniMapPiP;
